import { Link, Navigate, useRouterState } from '@tanstack/react-router';
import { ShieldAlert, ShieldCheck, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/page-header';
import { Button, Card, CardContent } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { TwoStepCard } from './two-step-card';

/** אבטחת החשבון — לכל תפקיד (גם מנהל וטכנאי, שאין להם הגדרות). */
export function SecurityPage() {
  const { t } = useTranslation('auth');
  const { session } = useAuth();
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <PageHeader title={t('mfa.page.title')} description={t('mfa.page.description')} />
      {session?.mfaRequiredSetup ? (
        <div role="alert" className="flex gap-3 rounded-(--radius-lg) border border-warning-border bg-warning-subtle p-4 text-sm text-fg">
          <ShieldAlert className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
          <span>
            <b className="block">{t('mfa.forced.title')}</b>
            {t('mfa.forced.description')}
          </span>
        </div>
      ) : null}
      <TwoStepCard />
    </div>
  );
}

/**
 * מנהל שהבעלים חייב אותו ב-2FA ועוד לא הקים — רק עמוד האבטחה פתוח לו (השרת
 * מחזיר 403 על כל השאר ממילא; זה רק כדי שלא יראה מסכים שבורים).
 */
export function MfaSetupGate({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (session?.mfaRequiredSetup && path !== '/security') return <Navigate to="/security" replace />;
  return <>{children}</>;
}

const HIDE_KEY = 'craftmind.mfa.recommend.hidden';

/** המלצה לבעלים בלי 2FA — בראש הסקירה, עד שמפעילים או מסתירים. */
export function MfaRecommendation() {
  const { t } = useTranslation(['auth', 'common']);
  const { session } = useAuth();
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDE_KEY) === '1';
    } catch {
      return false;
    }
  });
  if (!session || session.mfaEnabled || session.user.role !== 'OWNER' || hidden) return null;
  return (
    <Card className="border-accent-border bg-accent-subtle">
      <CardContent className="flex flex-wrap items-center gap-3 py-4">
        <ShieldCheck className="size-6 shrink-0 text-accent" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-fg">{t('mfa.recommend.title')}</p>
          <p className="text-sm text-fg-muted">{t('mfa.recommend.description')}</p>
        </div>
        <Link to="/security" className="inline-flex h-9 items-center rounded-(--radius-md) bg-accent px-4 text-sm font-medium text-fg-on-accent hover:opacity-90" data-track="mfa.recommend">
          {t('mfa.recommend.action')}
        </Link>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('common:actions.close')}
          onClick={() => {
            setHidden(true);
            try {
              localStorage.setItem(HIDE_KEY, '1');
            } catch {
              /* חסום */
            }
          }}
        >
          <X aria-hidden />
        </Button>
      </CardContent>
    </Card>
  );
}

/** מפעיל בלי 2FA — הקונסולה נעולה (השרת מחזיר 403); מסבירים ומפנים להפעלה. */
export function AdminMfaPrompt() {
  const { t } = useTranslation('auth');
  return (
    <Card className="mx-auto max-w-xl">
      <CardContent className="space-y-3 py-6 text-center">
        <ShieldAlert className="mx-auto size-8 text-warning" aria-hidden />
        <h1 className="text-lg font-semibold text-fg">{t('mfa.admin.title')}</h1>
        <p className="text-sm text-fg-muted">{t('mfa.admin.description')}</p>
        <Link to="/security" className="inline-flex h-10 items-center rounded-(--radius-md) bg-accent px-5 text-sm font-medium text-fg-on-accent hover:opacity-90">
          {t('mfa.admin.action')}
        </Link>
      </CardContent>
    </Card>
  );
}
